import { MarketDistributionCard } from '@src/components/MarketDistribution/MarketDistributionTabs';
import { IWouldCard } from '@src/components/OrderAnalytics/IWouldCard';
import { showIWouldAnalytics } from '@src/components/OrderAnalytics/IWouldCard/utils';
import { OrderTrajectoryCharts, shouldDisplayTrajectory } from '@src/components/OrderTrajectoryChart';
import { useFeatureFlag } from '@src/hooks';
import { useLatestOrderAnalytics } from '@src/hooks/useLatestOrderAnalytics';
import {
  BLOTTER_TABLE_FILTERS_CONTAINER_ID,
  Flex,
  LoaderTalos,
  Portal,
  useSecurity,
  type CustomerOrder,
  type Order,
} from '@talos/kyoko';
import { KeyMetricsCard } from '../../../../components/OrderAnalytics/KeyMetricsCard';
import { TradingCard } from '../../../../components/OrderAnalytics/TradingCard';
import { OrderExecutionCharts } from '../../../../components/OrderExecutionChart';
import { TradePredictionsWrapper } from '../../../../components/TradePredictions/TradePredictionsWrapper';
import { ANALYTICS_WIDTH } from '../../../../styles/dimensions';
import { TopBarUtils } from './TopBarUtils';
import { AnalyticsWrapper } from './styles';

export interface AnalyticsTabProps {
  order: Order;
  customerOrder?: CustomerOrder;
  isOrderMultileg: boolean;
  width: number;
}

export function AnalyticsTab({ order, customerOrder, isOrderMultileg, width }: AnalyticsTabProps) {
  const orderSecurity = useSecurity(order.Symbol);
  const { enableOrderTrajectoryCharts, enableIWouldAnalytics } = useFeatureFlag();

  const analytics = useLatestOrderAnalytics({
    order: order,
    tag: 'AnalyticsTab',
  });

  if (orderSecurity == null) {
    return <LoaderTalos />;
  }

  const isWideScreen = width > 1200;
  // whether 'primary column' is wide
  // 560px comes from max-width of the second column
  const isColumnWide = width - (isWideScreen ? 560 : 0) > 1000;

  return (
    <>
      <Portal portalId={BLOTTER_TABLE_FILTERS_CONTAINER_ID}>
        <TopBarUtils order={order} customerOrder={customerOrder} latestAnalytics={analytics.latestAnalytics} />
      </Portal>
      <AnalyticsWrapper>
        <Flex gap="spacing8" flexDirection={isWideScreen ? 'row' : 'column'}>
          <Flex
            flex={isWideScreen ? '1' : '1 0 auto'}
            flexDirection="column"
            gap="spacing8"
            overflow="hidden"
            minWidth={isWideScreen ? undefined : `${ANALYTICS_WIDTH}px`}
          >
            <OrderExecutionCharts
              isMultileg={isOrderMultileg}
              order={order}
              strategy={order.Strategy}
              height={300}
              resizeAware
              analytics={analytics}
            />
            {enableOrderTrajectoryCharts && shouldDisplayTrajectory(order) && (
              <OrderTrajectoryCharts
                isMultileg={isOrderMultileg}
                order={order}
                strategy={order.Strategy}
                height={500}
                resizeAware
                analytics={analytics}
              />
            )}
            <Flex gap="spacing8" flexDirection={isColumnWide ? 'row' : 'column'}>
              {enableIWouldAnalytics && showIWouldAnalytics(order) && (
                <IWouldCard order={order} isMultileg={isOrderMultileg} analytics={analytics} flex={1} />
              )}
              {!isOrderMultileg && (
                <TradingCard order={order} isMultileg={isOrderMultileg} analytics={analytics} flex={1} />
              )}
            </Flex>
          </Flex>

          <Flex
            flex={1}
            flexDirection="column"
            gap="spacing8"
            maxWidth={isWideScreen ? `${ANALYTICS_WIDTH}px` : undefined}
            minWidth={isWideScreen ? undefined : `${ANALYTICS_WIDTH}px`}
          >
            <TradePredictionsWrapper
              order={order}
              isOrderMultiLeg={isOrderMultileg}
              latestAnalytics={analytics.latestAnalytics}
            />
            <KeyMetricsCard isMultileg={isOrderMultileg} order={order} analytics={analytics} />
            <MarketDistributionCard
              unifiedLiquidity={order.unifiedLiquidity}
              order={order}
              isMultileg={isOrderMultileg}
            />
            {isOrderMultileg && <TradingCard order={order} isMultileg={isOrderMultileg} analytics={analytics} />}
          </Flex>
        </Flex>
      </AnalyticsWrapper>
    </>
  );
}
